import {
  AppRoleEnum,
  CompanyContractPlan,
  FeatureNameEnum,
} from '@ulysses-inc/harami_api_client'
import { localStorageKeys } from 'src/constants/localStorageKeys'
import { getContractPlan } from 'src/features/auth/getContractPlan'
import { useEnabledFeatures } from 'src/features/featureFlags/useFeatureFlags'
import { appRole } from 'src/libraries/roles/appRole'
import { appEnv } from 'src/util/appEnv'

/**
 * 機能の利用可否を判定する際の単位となるもの
 */
export type Feature =
  | 'DUMMY_FEATURE_fullRestriction' // ユニットテスト用のダミー (制約あり)
  | 'DUMMY_FEATURE_noRestriction' // ユニットテスト用のダミー (制約なし)
  | 'approvalFlows'
  | 'approvals'
  | 'companySettings'
  | 'dashboard'
  | 'dynamicSheet'
  | 'employees'
  | 'devices' // 共用端末
  | 'excelConversionEdit'
  | 'excelConversionRead'
  | 'improves' // カイゼン・ラベルどちらも含む
  | 'masters'
  | 'multipleChoiceSets'
  | 'places' // 現場・現場グループどちらも含む
  | 'reportExports'
  | 'reports'
  | 'scheduledReportSummary'
  | 'schedules'
  | 'tableSection' // 表セクション
  | 'templates'
  | 'users' // ユーザー・ユーザーグループどちらも含む
  | 'labelCheck'
  | 'analytics'
  | 'reportTrace' // 回答検索
  | 'shipmentReadinessOverview'
  | 'multiLanguageSupport' // 多言語対応
  | 'bulkReportExport' // 一括出力(複数ひな形)
  | 'monitoringDashboardAlpha' // 新実施状況 アルファ版・検証用
  | 'taskSchedules' // 作業指示（仮UI・pos-E の検証用）

/**
 * ある機能を利用するにあたって、どのような条件を満たす必要があるかを定義したもの。
 * 条件はANDで結合される。
 * e.g. 契約プラン、ユーザーロール、フィーチャーフラグの3つが指定された場合は、3つすべてを満たす必要がある。
 */
type Setting = {
  eligibleFeatureFlag?: FeatureNameEnum
  eligiblePlans?: CompanyContractPlan[] // いずれかのプランに当てはまればよい
  eligibleRoles?: AppRoleEnum[] // いずれかのロールに当てはまればよい
}

export type Settings = Record<Feature, Setting>

/**
 * unknown は plan/role を満たしつつ flag 取得中で確定できない状態。
 * flag 層の CanUse とは別の型にする(層ごとに verdict の意味が異なる)。
 */
export type FeatureAvailability = 'yes' | 'no' | 'unknown'

const roles = {
  leaderOrMore: [AppRoleEnum.LEADER, AppRoleEnum.EDITOR, AppRoleEnum.OWNER],
  editorOrMore: [AppRoleEnum.EDITOR, AppRoleEnum.OWNER],
  owner: [AppRoleEnum.OWNER],
}

export const settings: Settings = {
  DUMMY_FEATURE_fullRestriction: {
    eligibleFeatureFlag: FeatureNameEnum.DUMMY_1, // テストコード側と揃っていればなんでもよい
    eligiblePlans: [CompanyContractPlan.PREMIUM],
    eligibleRoles: roles.owner,
  },
  DUMMY_FEATURE_noRestriction: {},
  // 承認フロー
  approvalFlows: {
    eligibleRoles: roles.owner,
  },
  // 承認
  approvals: {
    eligibleRoles: roles.leaderOrMore,
  },
  // アプリ設定
  companySettings: {
    eligibleRoles: roles.owner,
  },
  // ダッシュボード
  dashboard: {
    eligibleRoles: roles.leaderOrMore,
  },
  // 表形式
  dynamicSheet: {
    eligiblePlans: [CompanyContractPlan.BASIC, CompanyContractPlan.PREMIUM],
    eligibleRoles: roles.editorOrMore,
  },
  // 従業員
  employees: {
    eligibleRoles: roles.owner,
  },
  // 共用端末 (kiosk)
  // feature flag ではなく、カミナシID (Rump) 側で集中管理されるロールアウトフラグで制御する。
  // フラグは GET /loginUser/context の kioskRollout として取得し AuthGuard が localStorage に
  // 保存したものを、getFeatureAvailability 内の個別判定で参照する。
  devices: {
    eligibleRoles: roles.owner,
  },
  // Excel変換 (編集権限あり)
  excelConversionEdit: {
    eligiblePlans: [
      CompanyContractPlan.OLD_BASIC,
      CompanyContractPlan.BASIC,
      CompanyContractPlan.PREMIUM,
    ],
    eligibleRoles: roles.editorOrMore,
  },
  // Excel変換 (閲覧権限のみ)
  excelConversionRead: {
    eligiblePlans: [
      CompanyContractPlan.OLD_BASIC,
      CompanyContractPlan.BASIC,
      CompanyContractPlan.PREMIUM,
    ],
    eligibleRoles: roles.leaderOrMore,
  },
  // カイゼン・ラベル
  improves: {
    eligiblePlans: [CompanyContractPlan.OLD_BASIC, CompanyContractPlan.PREMIUM],
    eligibleRoles: roles.leaderOrMore,
  },
  // マスタ
  masters: {
    eligiblePlans: [CompanyContractPlan.BASIC, CompanyContractPlan.PREMIUM],
    eligibleRoles: roles.editorOrMore,
  },
  // セット選択肢
  multipleChoiceSets: {
    eligibleRoles: roles.editorOrMore,
  },
  // 現場・現場グループ
  places: {
    eligibleRoles: roles.editorOrMore,
  },
  // レポート
  reports: {
    eligibleRoles: roles.leaderOrMore,
  },
  // レポート出力
  reportExports: {
    eligiblePlans: [
      CompanyContractPlan.OLD_BASIC,
      CompanyContractPlan.BASIC,
      CompanyContractPlan.PREMIUM,
    ],
    eligibleRoles: roles.leaderOrMore,
  },
  // 実施状況
  scheduledReportSummary: {
    eligibleRoles: roles.leaderOrMore,
  },
  // スケジュール
  schedules: {
    eligibleRoles: roles.editorOrMore,
  },
  // 表セクション
  tableSection: {
    eligiblePlans: [CompanyContractPlan.BASIC, CompanyContractPlan.PREMIUM],
  },
  // ひな形
  templates: {
    eligibleRoles: roles.editorOrMore,
  },
  // ユーザー・ユーザーグループ
  users: {
    eligibleRoles: roles.owner,
  },
  // ラベル検査
  labelCheck: {
    eligibleRoles: roles.editorOrMore,
  },
  // 分析(Vizion)
  analytics: {
    eligibleFeatureFlag: FeatureNameEnum.VIZION,
    eligibleRoles: roles.leaderOrMore,
  },
  // 回答検索
  reportTrace: {
    eligiblePlans: [
      CompanyContractPlan.OLD_BASIC,
      CompanyContractPlan.BASIC,
      CompanyContractPlan.PREMIUM,
    ],
  },
  // 出荷判定PoC
  shipmentReadinessOverview: {
    eligibleFeatureFlag: FeatureNameEnum.SHIPMENT_READINESS_OVERVIEW,
    eligibleRoles: roles.editorOrMore,
  },
  // 多言語対応
  multiLanguageSupport: {
    eligiblePlans: [CompanyContractPlan.PREMIUM],
    eligibleRoles: roles.editorOrMore,
  },
  // 一括出力(複数ひな形)。plan は reportExports と同じだが、role は reportExports の
  // leader 以上より狭く管理者のみ。API 側も POST /reports/exports は leader 以上、
  // POST /reports/exports/bulk は管理者限定と分かれているため、条件を揃える。
  bulkReportExport: {
    eligiblePlans: [
      CompanyContractPlan.OLD_BASIC,
      CompanyContractPlan.BASIC,
      CompanyContractPlan.PREMIUM,
    ],
    eligibleRoles: roles.owner,
  },
  // 新実施状況 アルファ版・検証用。flag `monitoringDashboardAlpha` で出し分ける。
  // TODO: 検証終了時はこの設定・メニュー・ルートと src/features/monitoringDashboardAlpha を削除する
  monitoringDashboardAlpha: {
    eligibleFeatureFlag: FeatureNameEnum.MONITORING_DASHBOARD_ALPHA,
    eligibleRoles: roles.leaderOrMore,
  },
  // 作業指示（仮UI）。flag `taskSchedule` で出し分ける。管理画面で作業指示を作り、記録アプリの「やること」で確かめるための仮の画面。
  // TODO: 本実装（HRMSHI-132）ができたらこの設定・メニュー・ルートと src/features/taskSchedules を差し替える
  taskSchedules: {
    eligibleFeatureFlag: FeatureNameEnum.TASK_SCHEDULE,
    eligibleRoles: roles.editorOrMore,
  },
}

/**
 * 限定されたユーザーにのみ開放している機能について、利用可能か判定するカスタムフック。
 * 判定にあたっては、契約プラン、ユーザーロール、FeatureFlagのすべてが勘案される。
 */
export const useLimitedFeatureAvailability = () => {
  const { features: enabledFeatures, isLoading: isFeatureFlagsLoading } =
    useEnabledFeatures()

  const isLobsterEnabled = getIsLobsterEnabled()
  const kioskRollout = getKioskRollout()

  const getFeatureAvailability = (feature: Feature): FeatureAvailability => {
    // ラベル検査はLobsterの使用が許可されていない場合は使えない
    if (feature === 'labelCheck' && !isLobsterEnabled) return 'no'

    // 共用端末はカミナシID (Rump) 側のロールアウトフラグが有効でない場合は使えない
    // ローカル環境では従来の feature flag 判定と同様に常に利用可能とする
    if (feature === 'devices' && !appEnv.isLocal && !kioskRollout) return 'no'

    const { eligiblePlans, eligibleRoles, eligibleFeatureFlag } =
      settings[feature]

    const hasValidContractPlan = (() => {
      const contractPlan = getContractPlan()
      if (!eligiblePlans) return true
      if (!contractPlan) return false
      return eligiblePlans.includes(contractPlan)
    })()

    const hasValidRole = (() => {
      const loggedInUserRole = appRole()
      if (!eligibleRoles) return true
      if (!loggedInUserRole) return false
      return eligibleRoles.includes(loggedInUserRole)
    })()

    // plan・role は同期的に確定するので、flag の取得を待たずに no を返す
    if (!hasValidContractPlan || !hasValidRole) return 'no'

    if (!eligibleFeatureFlag) return 'yes'
    // ローカル環境ではFeatureFlagの設定に関わらず常に利用可能とする
    if (appEnv.isLocal || enabledFeatures.includes(eligibleFeatureFlag)) {
      return 'yes'
    }
    return isFeatureFlagsLoading ? 'unknown' : 'no'
  }

  const isFeatureAvailable = (feature: Feature): boolean =>
    getFeatureAvailability(feature) === 'yes'

  return { isFeatureAvailable, getFeatureAvailability }
}

const getIsLobsterEnabled = (): boolean => {
  return (
    localStorage.getItem(localStorageKeys.loginUserIsLobsterEnabled) === '1'
  )
}

const getKioskRollout = (): boolean => {
  return localStorage.getItem(localStorageKeys.loginUserKioskRollout) === '1'
}
